import React, { FC, useEffect, useState } from 'react';
import { Variant } from 'shared/types/product';
import { Category } from 'shared/types/product/Category';
import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Gallery from 'components/commercetools-ui/organisms/gallery';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { minitablet } from 'helpers/utils/screensizes';
import { useProduct } from 'frontastic';
import AdditionalInfo from './components/additional-info';
import ProductInformation from './components/product-information';
import { UIProduct } from './types';
import { HomeIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import ProductSpecification from './components/product-specification';
import { GENERIC_ATTRIBUTES } from 'helpers/constants/attributes';
import WhiteOverlay from 'components/commercetools-ui/atoms/overlay/white-overlay';
import BundleProducts from './components/bundleProducts';
import SpareParts from './components/spare-parts';
import { getCategoryLink } from './helpers/getCategoryURL';
import SidebarPDP from './components/sidebar';
import { localeMapping } from 'helpers/constants/localeMapping';
import Spinner from '../../spinner';
import { PRODUCT_RANGES } from 'helpers/constants/collection-names';
import { sessionStorageKeys } from 'helpers/constants/sessionStorageKeys';


export interface ProductDetailsProps {
  product: UIProduct;
  variant: Variant;
  url?: string;
  onChangeVariant: (sku: string) => void;
  inModalVersion?: boolean;
  setIsOpen?: (value: boolean) => void;
  onAddToCart?: () => void;
}

const ProductDetails: FC<ProductDetailsProps> = ({
  product,
  variant,
  url,
  onChangeVariant,
  inModalVersion,
  setIsOpen,
  onAddToCart,
}) => {
  const [loading, setLoading] = useState(true);
  const [isSpinnerVisible, setSpinner] = useState(false);
  const [isTabletSize] = useMediaQuery(minitablet);
  const [category, setCategory] = useState<Category>();
  const [inventoryData, setInventoryData] = useState();

  const { categories } = useProduct();
  const searchParams = useSearchParams();
  //const categoryLink = searchParams.get('category') as string;
  //const collectionLink = searchParams.get('collection') as string;
  let categoryLink: string | null | undefined = '';
  let collectionLink: string | null | undefined = '';
  const categoryURLHelper = getCategoryLink(product?.categories?.[0]?.categoryId?.toString());
  const { locale } = useParams();
  const localeKey = localeMapping[locale];
  
  useEffect(() => {
    if (product && categories && !category) {
      const currentCategory = categories.find((cat) =>
        cat.subCategories?.find((sub) => sub.categoryId === product.categories?.[0]?.categoryId),
      );
      setCategory(currentCategory);
    }
  }, [categories, category, product, product?.categories]);

  

  const generateBreadcrumbs = () => {
    if(!categoryLink) categoryLink = searchParams.get('category') as string;
    if(!collectionLink) collectionLink = searchParams.get('collection') as string;
    if(!categoryLink && !collectionLink){
      if (typeof window !== 'undefined') {
        categoryLink = sessionStorage.getItem(sessionStorageKeys.categoryLink);
        collectionLink = sessionStorage.getItem(sessionStorageKeys.collectionLink);
      }
    }
    if(collectionLink && PRODUCT_RANGES.some(range => range.toLowerCase() === collectionLink!.split('/')[2].toLowerCase()))
    {
      return [
          <span key='1' style={{ cursor: 'default' }} className="text-14 font-normal leading-4 text-[#666] capitalize">
            Collection
          </span>,
          <Link key='2' link={collectionLink}>
            <span className="text-14 font-normal leading-4 text-[#666] capitalize">
            {collectionLink.split('/')[2]}
            </span>
          </Link>
      ];
    }

    if(categoryLink && ['bathroom','kitchen laundry','independent living','clark','dorf'].includes(categoryLink.split('/')[1].toLowerCase().replaceAll('-',' ').replaceAll('and','&')))
    {
      if(categoryLink.split('/')[2])
      {    
        return [
          <Link key='1' link={`${categoryLink.split('/').splice(0,2).join('/')}/`}>
            <span className="text-14 font-normal leading-4 text-[#666] capitalize">
              {categoryLink.split('/')[1].replaceAll('-',' ').replaceAll('and','&')}
            </span>
          </Link>,
          <Link key='2' link={categoryLink}>
            <span className="text-14 font-normal leading-4 text-[#666] capitalize">
              {categoryLink.split('/')[2].replaceAll('-',' ')}
            </span>
          </Link>
        ];
      }
      else
      {
        return [
            <Link key='1' link={`${categoryLink.split('/').splice(0,2).join('/')}/`}>
              <span className="text-14 font-normal leading-4 text-[#666] capitalize">
                {categoryLink.split('/')[1].replaceAll('-',' ').replaceAll('and','&')}
              </span>
            </Link>
        ];}
      }

    if(categoryURLHelper.split('/').length == 4)
    {    
      return [
        <Link key='1' link={`${categoryURLHelper.split('/').splice(0,2).join('/')}/`}>
          <span className="text-14 font-normal leading-4 text-[#666] capitalize">
            {categoryURLHelper.split('/')[1].replaceAll('-',' ').replaceAll('and','&')}
          </span>
        </Link>,
        <Link key='2' link={categoryURLHelper}>
          <span className="text-14 font-normal leading-4 text-[#666] capitalize">
            {categoryURLHelper.split('/')[2].replaceAll('-',' ')}
          </span>
        </Link>
      ];
    }
    else
    {
      return [
          <Link key='1' link={categoryURLHelper}>
            <span className="text-14 font-normal leading-4 text-[#666] capitalize">
              {categoryURLHelper.split('/')[1].replaceAll('-',' ').replaceAll('and','&')}
            </span>
          </Link>
      ];
    }
  }
    

  const componentsReferences: string | undefined =
    variant && variant.attributes
      ? variant.attributes['item-attributes']?.find(
          (attribute: { name: string; value?: string }) => attribute.name === 'bundle-references',
        )?.value
      : undefined;

  const componentsInventory = Array.isArray(componentsReferences)
    ? componentsReferences
        .map((code: string) => {
          return (inventoryData as any)?.find((item: any) => item.itemcode === code);
        })
        .filter((item) => item !== undefined)
    : [];

  const sparePartsReferences: string | undefined =
    variant && variant.attributes
      ? variant.attributes['item-attributes']?.find(
          (attribute: { name: string; value?: string }) => attribute.name === 'spare-references',
        )?.value
      : undefined;

  const sku = variant?.attributes?.['product-code'] || '';
  const color = variant?.attributes?.colour;
  return (
    <>
      {isSpinnerVisible && <Spinner />}
        <div className="relative left-full z-[20] w-screen -translate-x-full lg:grid lg:grid-cols-12 ">
          {loading && <WhiteOverlay />}
          {category && (
          <Breadcrumb
            Separator={<ChevronRightIcon className="mt-4 md:w-14 lg:w-16 xl:w-14 2xl:w-16" />}
            listClassName="!gap-x-16 !px-0"
            className="absolute left-24 col-span-12 mb-32 mt-84 hidden px-0 md:left-28 md:mt-20 md:block md:px-0 lg:left-48 lg:px-16 xl:left-60 xl:px-16 2xl:left-80 2xl:px-16 3xl:left-80"
          >
            <Link link={'/'}>
              <HomeIcon className="mt-8 w-20" />
            </Link>

            {generateBreadcrumbs()}

          </Breadcrumb>
        )}
        <div className="!z-[50] flex flex-col lg:col-span-9 lg:col-start-4">
          <div className="!z-[50] left-24 mt-32 px-24 md:left-28 md:px-28 lg:absolute lg:left-48 lg:mt-[5rem] lg:px-0 xl:left-60 xl:mt-[3.5rem] xl:pr-60 2xl:left-80 2xl:mt-[4rem] 2xl:pr-80">
            <ProductInformation
              product={product}
              variant={variant}
              onChangeVariant={onChangeVariant}
              inModalVersion={inModalVersion}
            />
          </div>
          <div className="hidden lg:block !w-fit left-24 mt-32 px-24 md:left-28 md:px-28 lg:absolute lg:left-64 lg:mt-[14.75rem] lg:px-0 xl:left-76 xl:mt-[12.5rem] xl:pr-60 2xl:left-96 2xl:mt-[16rem] 3xl:mt-[14rem] 2xl:pr-80">
          <SidebarPDP />
        </div>
          <div className="mt-[1rem] lg:mt-[15.5rem] xl:mt-[14rem] 2xl:mt-[16rem]">
            <div className="px-0 md:px-0 lg:pl-30 lg:pr-48 xl:pl-0 xl:pr-60 2xl:pl-0 2xl:pr-80">
              <Gallery images={variant?.images ?? []} setLoading={setLoading} sku={sku} color={color} />
            </div>
            <div className="lg:hidden !w-fit left-24 mt-48 px-24 md:left-28 md:px-28">
          <SidebarPDP />
        </div>
            {!inModalVersion && (
              <div id='product-overview-scroll' className="gap-y-34 pr-24 md:px-28 lg:pl-30 lg:pr-48 xl:pl-0 xl:pr-60 2xl:pr-80">
                <AdditionalInfo
                  productspec={variant?.attributes}
                  features={
                    variant?.attributes &&
                    variant?.attributes[GENERIC_ATTRIBUTES].filter((att: { name: string }) => att.name === 'features')[0]
                      .value?.[`${localeKey}`]
                  }
                  description={product?.description ?? ''}
                />
              </div>
            )}
          </div>
          <ProductSpecification setSpinner={setSpinner} product={product} variant={variant} />
          <div>
            {componentsReferences && componentsReferences.length > 0 &&  (
              <BundleProducts bundleReferences={componentsReferences} componentsInventory={componentsInventory} />
            )}
          </div>
          <div className="">
            {sparePartsReferences && sparePartsReferences.length > 0 && (
              <SpareParts sparePartsReferences={sparePartsReferences} />
            )}
          </div>
        </div>
        </div>
      </>
  );
};

export default ProductDetails;
